import { useState } from 'react';
import type { FormEvent } from 'react';
import { OpsApiError } from '../api/client.ts';
import {
  useSoftDeleteAccount,
  useSuspendAccount,
  useUnsuspendAccount,
} from '../api/useAdmin.ts';
import { Modal } from './Modal.tsx';
import { useToast, coerceErrorMessage } from './Toast.tsx';
import { formatLocalTimestamp } from './formatters.ts';
import { useT } from '../i18n/index.ts';
import { Button, Chip, FormField, Input, Panel } from '../ui/index.ts';
import type { ChipTone } from '../ui/index.ts';

/**
 * Account lifecycle card — suspend / unsuspend / soft-delete. Epic #109
 * P2.10.
 *
 * Shown on AccountDetail right after the Summary card so ops can see
 * the status chip at the top and take action without scrolling. The
 * card gates its actions on the current state:
 *   - active: Suspend + Delete.
 *   - suspended: Unsuspend + Delete (suspension reason displayed).
 *   - deleted: read-only; no further actions permitted from the UI.
 *
 * All three mutations are type-to-confirm to match the balance-adjust
 * and gift-card-revoke patterns — the email is the confirmation token
 * for suspend/delete (less typo-prone than the opaque account id).
 */

interface Props {
  readonly accountId: string;
  readonly email: string;
  readonly suspendedAt: string | null;
  readonly suspensionReason: string | null;
  readonly deletedAt: string | null;
}

type DraftAction = 'suspend' | 'unsuspend' | 'delete';

interface Draft {
  readonly action: DraftAction;
  readonly reason: string;
}

function statusTone(
  suspendedAt: string | null,
  deletedAt: string | null,
): ChipTone {
  if (deletedAt !== null) return 'bad';
  if (suspendedAt !== null) return 'warn';
  return 'ok';
}

export function AccountLifecycleCard({
  accountId,
  email,
  suspendedAt,
  suspensionReason,
  deletedAt,
}: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const suspend = useSuspendAccount();
  const unsuspend = useUnsuspendAccount();
  const softDelete = useSoftDeleteAccount();

  const [suspendReason, setSuspendReason] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [typed, setTyped] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);

  const busy = suspend.isPending || unsuspend.isPending || softDelete.isPending;
  const tone = statusTone(suspendedAt, deletedAt);
  const statusLabel =
    deletedAt !== null
      ? t('account_lifecycle.status.deleted')
      : suspendedAt !== null
      ? t('account_lifecycle.status.suspended')
      : t('account_lifecycle.status.active');
  const canSuspend = suspendedAt === null && deletedAt === null;
  const canUnsuspend = suspendedAt !== null && deletedAt === null;
  const canDelete = deletedAt === null;
  const typedMatches = typed.trim() === email;

  function openConfirm(action: DraftAction): void {
    setModalError(null);
    setTyped('');
    setDraft({
      action,
      reason: action === 'suspend' ? suspendReason.trim() : '',
    });
  }

  function closeConfirm(): void {
    setDraft(null);
    setTyped('');
    setModalError(null);
  }

  async function handleConfirm(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setModalError(null);
    if (draft === null) return;
    if (!typedMatches) {
      setModalError(t('account_lifecycle.confirm.type_mismatch', { email }));
      return;
    }
    try {
      if (draft.action === 'suspend') {
        await suspend.mutateAsync({
          accountId,
          // Omit `reason` entirely when blank so the wire payload
          // matches `SuspendAccountRequest.reason?: string`. A prior
          // version sent `{ reason: '' }`, which the backend happened
          // to normalize to null but violated the declared contract.
          body: draft.reason.length > 0 ? { reason: draft.reason } : {},
        });
        toast.success(t('account_lifecycle.toast.suspended', { email }));
        setSuspendReason('');
      } else if (draft.action === 'unsuspend') {
        await unsuspend.mutateAsync({ accountId });
        toast.success(t('account_lifecycle.toast.unsuspended', { email }));
      } else {
        await softDelete.mutateAsync({ accountId });
        toast.success(t('account_lifecycle.toast.deleted', { email }));
      }
      closeConfirm();
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setModalError(message);
    }
  }

  return (
    <Panel
      caption={t('account_lifecycle.title')}
      actions={
        <Chip tone={tone} testId="account-lifecycle-status">
          {statusLabel}
        </Chip>
      }
      padded
      testId="account-lifecycle-card"
    >
      <div className="flex flex-col gap-4">
        {suspendedAt !== null && deletedAt === null ? (
          <p
            role="status"
            className="rounded-lg border border-warn-line bg-warn-dim px-4 py-3 text-dense text-warn"
            data-testid="account-lifecycle-suspension-banner"
          >
            {t('account_lifecycle.suspended.banner', {
              iso: formatLocalTimestamp(suspendedAt, { seconds: true }),
              reason: suspensionReason ?? t('account_lifecycle.suspended.no_reason'),
            })}
          </p>
        ) : null}

        {deletedAt !== null ? (
          <p
            role="status"
            className="rounded-lg border border-bad-line bg-bad-dim px-4 py-3 text-dense text-bad-bright"
            data-testid="account-lifecycle-deleted-banner"
          >
            {t('account_lifecycle.deleted.banner', {
              iso: formatLocalTimestamp(deletedAt, { seconds: true }),
            })}
          </p>
        ) : null}

        {canSuspend ? (
          <FormField
            label={t('account_lifecycle.suspend.reason_label')}
            fieldId="account-lifecycle-suspend-reason"
            hint={t('account_lifecycle.suspend.reason_hint')}
          >
            <Input
              id="account-lifecycle-suspend-reason"
              type="text"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.currentTarget.value)}
              placeholder={t('account_lifecycle.suspend.reason_placeholder')}
              data-testid="account-lifecycle-suspend-reason"
              disabled={busy}
            />
          </FormField>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          {canSuspend ? (
            <Button
              onClick={() => openConfirm('suspend')}
              disabled={busy}
              data-testid="account-lifecycle-suspend"
            >
              {t('account_lifecycle.suspend.button')}
            </Button>
          ) : null}
          {canUnsuspend ? (
            <Button
              onClick={() => openConfirm('unsuspend')}
              disabled={busy}
              data-testid="account-lifecycle-unsuspend"
            >
              {t('account_lifecycle.unsuspend.button')}
            </Button>
          ) : null}
          {canDelete ? (
            <Button
              variant="danger"
              onClick={() => openConfirm('delete')}
              disabled={busy}
              data-testid="account-lifecycle-delete"
            >
              {t('account_lifecycle.delete.button')}
            </Button>
          ) : null}
        </div>
      </div>

      {draft !== null ? (
        <Modal
          isOpen
          onClose={() => {
            if (!busy) closeConfirm();
          }}
          titleId="account-lifecycle-confirm-title"
          testId="account-lifecycle-modal"
        >
          <form
            onSubmit={handleConfirm}
            className="flex flex-col gap-5 p-6"
            noValidate
          >
            <h2
              id="account-lifecycle-confirm-title"
              className="text-title text-paper"
            >
              {t(
                draft.action === 'suspend'
                  ? 'account_lifecycle.confirm.title_suspend'
                  : draft.action === 'unsuspend'
                  ? 'account_lifecycle.confirm.title_unsuspend'
                  : 'account_lifecycle.confirm.title_delete',
                { email },
              )}
            </h2>
            <p className="text-body text-paper-mid">
              {t(
                draft.action === 'suspend'
                  ? 'account_lifecycle.confirm.body_suspend'
                  : draft.action === 'unsuspend'
                  ? 'account_lifecycle.confirm.body_unsuspend'
                  : 'account_lifecycle.confirm.body_delete',
                { email },
              )}
            </p>
            {draft.action === 'suspend' && draft.reason.length > 0 ? (
              <p className="rounded-lg bg-well px-3 py-2 text-dense text-paper-mid">
                {t('account_lifecycle.confirm.reason_preview', { reason: draft.reason })}
              </p>
            ) : null}
            <FormField
              label={t('account_lifecycle.confirm.type_email', { email })}
              fieldId="account-lifecycle-modal-typed"
            >
              <Input
                id="account-lifecycle-modal-typed"
                type="text"
                mono
                value={typed}
                onChange={(e) => setTyped(e.currentTarget.value)}
                placeholder={email}
                data-testid="account-lifecycle-modal-typed"
                autoComplete="off"
                disabled={busy}
              />
            </FormField>
            {modalError !== null ? (
              <p
                role="alert"
                className="text-label text-bad"
                data-testid="account-lifecycle-modal-error"
              >
                {modalError}
              </p>
            ) : null}
            <div className="flex items-center justify-end gap-3">
              <Button onClick={closeConfirm} disabled={busy}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant={draft.action === 'delete' ? 'danger' : 'primary'}
                disabled={!typedMatches}
                loading={busy}
                data-testid="account-lifecycle-modal-submit"
              >
                {busy
                  ? t('common.saving')
                  : t(
                      draft.action === 'suspend'
                        ? 'account_lifecycle.confirm.submit_suspend'
                        : draft.action === 'unsuspend'
                        ? 'account_lifecycle.confirm.submit_unsuspend'
                        : 'account_lifecycle.confirm.submit_delete',
                    )}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}
    </Panel>
  );
}
